import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Camera, Video, Calendar, Search, Filter, X, Image, Play, ArrowLeft } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { Lightbox, GalleryCard } from '../../components/gallery'
import { Section, Button } from '../../components/common'
import { formatDate } from '../../utils/helpers'

// Sample gallery data for when Firestore is empty
const sampleGalleries = [
  {
    id: '2',
    title: 'Youth Camp Summer 2024',
    description: 'An amazing week of fellowship, worship, and spiritual growth for our youth.',
    date: '2024-07-15',
    category: 'youth',
    coverImage: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=800',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1200', caption: 'Group Photo' },
      { type: 'video', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', caption: 'Camp Highlights' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200', caption: 'Fun Activities' },
    ],
  },
  {
    id: '3',
    title: 'Baptism Service',
    description: 'Celebrating new believers taking the step of baptism.',
    date: '2024-03-10',
    category: 'baptism',
    coverImage: 'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=800',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=1200', caption: 'Baptism' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1507692049790-de58290a4334?w=1200', caption: 'Celebration' },
    ],
  },
]

const categories = [
  { value: 'all', label: 'All Events' },
  { value: 'worship', label: 'Worship Services' },
  { value: 'youth', label: 'Youth Events' },
  { value: 'baptism', label: 'Baptisms' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'fellowship', label: 'Fellowship' },
  { value: 'special', label: 'Special Events' },
]

const Gallery = () => {
  const [selectedGallery, setSelectedGallery] = useState(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedYear, setSelectedYear] = useState('all')

  const { data: galleries, loading } = useCollection('galleries', {
    orderByField: 'date',
    orderDirection: 'desc',
  })

  const displayGalleries = galleries?.length > 0 ? galleries : sampleGalleries

  // Get unique years from galleries
  const years = useMemo(() => {
    const yearSet = new Set(displayGalleries.map(g => new Date(g.date).getFullYear()))
    return ['all', ...Array.from(yearSet).sort((a, b) => b - a)]
  }, [displayGalleries])

  // Filter galleries
  const filteredGalleries = useMemo(() => {
    return displayGalleries.filter(gallery => {
      const matchesSearch = !searchQuery ||
        gallery.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        gallery.description?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesCategory = selectedCategory === 'all' || gallery.category === selectedCategory

      const matchesYear = selectedYear === 'all' ||
        new Date(gallery.date).getFullYear().toString() === selectedYear.toString()

      return matchesSearch && matchesCategory && matchesYear && gallery.isPublished !== false
    })
  }, [displayGalleries, searchQuery, selectedCategory, selectedYear])

  const openLightbox = (mediaIndex) => {
    setLightboxIndex(mediaIndex)
    setLightboxOpen(true)
  }

  const totalPhotos = displayGalleries.reduce(
    (acc, g) => acc + (g.media?.filter(m => m.type === 'image').length || 0), 0
  )
  const totalVideos = displayGalleries.reduce(
    (acc, g) => acc + (g.media?.filter(m => m.type === 'video').length || 0), 0
  )

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1920"
            alt="Gallery"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-dark via-dark/90 to-dark/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-secondary-400 font-accent font-semibold uppercase tracking-wider">
              <Camera className="w-5 h-5" />
              Event Gallery
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mt-4 mb-6">
              Relive Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">Moments</span> Together
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Browse through our collection of photos and videos from church events, services, and special celebrations.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-white">
                <div className="p-2 bg-primary-500/20 rounded-lg">
                  <Image className="w-5 h-5 text-primary-400" />
                </div>
                <span className="text-2xl font-bold">{totalPhotos}</span>
                <span className="text-gray-400">Photos</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <div className="p-2 bg-secondary-500/20 rounded-lg">
                  <Video className="w-5 h-5 text-secondary-400" />
                </div>
                <span className="text-2xl font-bold">{totalVideos}</span>
                <span className="text-gray-400">Videos</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <div className="p-2 bg-accent-500/20 rounded-lg">
                  <Calendar className="w-5 h-5 text-accent-400" />
                </div>
                <span className="text-2xl font-bold">{displayGalleries.length}</span>
                <span className="text-gray-400">Events</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Content */}
      <Section className="bg-gray-50">
        <Section.Container>
          <AnimatePresence mode="wait">
            {selectedGallery ? (
              /* Gallery Detail View */
              <motion.div
                key="detail"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Back Button */}
                <button
                  onClick={() => setSelectedGallery(null)}
                  className="flex items-center gap-2 text-primary-600 font-medium mb-6 hover:text-primary-700"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Gallery
                </button>

                {/* Gallery Header */}
                <div className="mb-8">
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-2">
                    {selectedGallery.title}
                  </h2>
                  <div className="flex items-center gap-4 text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedGallery.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Image className="w-4 h-4" />
                      {selectedGallery.media?.filter(m => m.type === 'image').length || 0} Photos
                    </span>
                    <span className="flex items-center gap-1">
                      <Video className="w-4 h-4" />
                      {selectedGallery.media?.filter(m => m.type === 'video').length || 0} Videos
                    </span>
                  </div>
                  {selectedGallery.description && (
                    <p className="text-gray-600">{selectedGallery.description}</p>
                  )}
                </div>

                {/* Media Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {selectedGallery.media?.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer bg-gray-200"
                      onClick={() => openLightbox(index)}
                    >
                      {item.type === 'video' ? (
                        <div className="w-full h-full bg-dark-200 flex items-center justify-center">
                          <Play className="w-12 h-12 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                        </div>
                      ) : (
                        <img
                          src={item.thumbnail || item.url}
                          alt={item.caption || ''}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />

                      {/* Type Badge */}
                      {item.type === 'video' && (
                        <span className="absolute top-2 right-2 px-2 py-1 bg-secondary-500 text-white text-xs font-medium rounded-full">
                          Video
                        </span>
                      )}

                      {/* Caption */}
                      {item.caption && (
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-sm truncate">{item.caption}</p>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              /* Gallery List View */
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-8">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2"
                      >
                        <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>

                  {/* Category Filter */}
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>

                  {/* Year Filter */}
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>
                        {year === 'all' ? 'All Years' : year}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Gallery Grid */}
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
                  </div>
                ) : filteredGalleries.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredGalleries.map((gallery, index) => (
                      <motion.div
                        key={gallery.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <GalleryCard
                          gallery={gallery}
                          onClick={() => setSelectedGallery(gallery)}
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Camera className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-dark-100 mb-2">No galleries found</h3>
                    <p className="text-gray-500">Try adjusting your search or filters</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Section.Container>
      </Section>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        media={selectedGallery?.media || []}
        startIndex={lightboxIndex}
      />
    </>
  )
}

export default Gallery
